import { Zap } from 'lucide-react';
import { Badge } from '@/shared/ui/badge';
import { cn } from '@/shared/lib/utils';
import type { OrderStatus } from '../types';

interface StatusStyle {
  label: string;
  fg: string;
  bg: string;
  dot: string;
}

const STYLES: Record<OrderStatus, StatusStyle> = {
  PENDING: {
    label: 'New',
    fg: 'text-destructive',
    bg: 'bg-destructive/10',
    dot: 'bg-destructive',
  },
  SCHEDULED: {
    label: 'Scheduled',
    fg: 'text-amber-600 dark:text-amber-500',
    bg: 'bg-amber-50 dark:bg-amber-950/30',
    dot: 'bg-amber-500',
  },
  CONFIRMED: {
    label: 'Confirmed',
    fg: 'text-success',
    bg: 'bg-success/10',
    dot: 'bg-success',
  },
  PREPARING: {
    label: 'Preparing',
    fg: 'text-accent',
    bg: 'bg-accent/10',
    dot: 'bg-accent',
  },
  IN_DELIVERY: {
    label: 'Out for delivery',
    fg: 'text-accent',
    bg: 'bg-accent/10',
    dot: 'bg-accent',
  },
  READY_FOR_PICKUP: {
    label: 'Ready for pickup',
    fg: 'text-accent',
    bg: 'bg-accent/10',
    dot: 'bg-accent',
  },
  COMPLETED: {
    label: 'Completed',
    fg: 'text-muted-foreground',
    bg: 'bg-muted',
    dot: 'bg-muted-foreground',
  },
  CANCELLED: {
    label: 'Cancelled',
    fg: 'text-muted-foreground',
    bg: 'bg-muted',
    dot: 'bg-muted-foreground/60',
  },
};

interface Props {
  status: OrderStatus;
  autoAcceptedBySystem?: boolean;
  pulse?: boolean;
  className?: string;
}

export function OrderStatusBadge({ status, autoAcceptedBySystem, pulse, className }: Props) {
  const style = STYLES[status];
  return (
    <Badge className={cn(style.bg, style.fg, 'border-0 text-xs gap-1.5 font-medium', className)}>
      <span
        className={cn(
          'w-1.5 h-1.5 rounded-full shrink-0',
          style.dot,
          pulse && 'animate-pulse'
        )}
        aria-hidden
      />
      {style.label}
      {autoAcceptedBySystem && (
        <span className="inline-flex items-center gap-0.5 rounded-full bg-background/80 text-[10px] px-1.5 py-0.5 ml-1 text-muted-foreground font-medium">
          <Zap className="w-2.5 h-2.5" /> Auto
        </span>
      )}
    </Badge>
  );
}
